import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import StructuredData from '@/components/base/StructuredData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const category = categories.find((c) => c.slug === slug);
  const categoryComics = useMemo(
    () => comics.filter((c) => c.category === slug).sort((a, b) => b.updateDate.localeCompare(a.updateDate)),
    [slug]
  );

  const totalPages = Math.ceil(categoryComics.length / pageSize);
  const paginatedComics = categoryComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const categoryNames: Record<string, string> = {
    romance: '恋爱漫画',
    fantasy: '奇幻漫画',
    action: '动作漫画',
    thriller: '悬疑漫画',
    'slice-of-life': '日常漫画',
    comedy: '搞笑漫画',
    drama: '剧情漫画',
    completed: '完结漫画',
  };

  const catName = categoryNames[slug || ''] || slug || '';

  const categoryStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 韩漫在线`,
    description: `韩漫在线${catName}分类，共${categoryComics.length}部漫画，免费下拉式阅读。`,
    url: `https://hanmanonline.com/category/${slug}`,
    inLanguage: 'zh-CN',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanonline.com' },
        { '@type': 'ListItem', position: 2, name: catName },
      ],
    },
  };

  return (
    <main className="min-h-screen bg-background-100/70">
      <StructuredData data={categoryStructuredData} />
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryNames[slug || ''] || slug || '' },
          ]}
        />

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            {categoryNames[slug || ''] || '漫画分类'}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            共 {categoryComics.length} 部漫画
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
                cat.slug === slug
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
              }`}
            >
              <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        {paginatedComics.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {paginatedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </>
        ) : (
          <div className="text-center py-20 bg-background-50 rounded-lg border border-background-200">
            <i className="ri-inbox-line text-5xl text-foreground-300 block mb-4"></i>
            <p className="text-foreground-500">该分类暂无漫画</p>
            <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}